import { defineStore } from 'pinia'
import Vue from 'vue'
import downloader from '@/utils/downloader'

/**
 * 视频消息预热：收到消息后立即轮询 m3u8 + 后台静默下载原视频
 * - 缩略图 loading / 就绪状态由此 store 派发
 * - 下载完成后仅派发给组件更新 localFile，不自动播放
 */

const POLL_INTERVAL = 5000
const POLL_MAX_DURATION = 10 * 60 * 1000

export default defineStore('videoPrewarmStore', {
	state: () => ({
		// messageId -> { status, m3u8Url, localFile, downloading, downloadDone, _startedAt, _pollTimer }
		entries: {}
	}),
	actions: {
		prewarm(msg) {
			if (!msg || !msg.id) return
			const id = String(msg.id)
			if (this.entries[id]) return

			let content
			try { content = JSON.parse(msg.content || '{}') } catch (e) { return }
			const m3u8Url = content.m3u8Url
			const videoUrl = content.videoUrl

			Vue.set(this.entries, id, {
				status: m3u8Url ? 'polling' : 'idle',
				m3u8Url: m3u8Url || null,
				localFile: content.localFile || null,
				downloading: false,
				downloadDone: !!content.localFile,
				_startedAt: Date.now(),
				_pollTimer: null
			})

			if (m3u8Url) this._pollM3u8(id, m3u8Url)
			if (videoUrl && !content.localFile) this._silentDownload(id, videoUrl, content.fileName)
		},

		_pollM3u8(id, m3u8Url) {
			const tick = () => {
				const e = this.entries[id]
				if (!e || e.status === 'ready' || e.status === 'timeout') return

				if (Date.now() - e._startedAt > POLL_MAX_DURATION) {
					console.warn('[VIDEO_HLS] ✗ prewarm m3u8 超时放弃 messageId:', id)
					Vue.set(this.entries, id, { ...e, status: 'timeout', _pollTimer: null })
					return
				}

				fetch(m3u8Url, { method: 'HEAD' })
					.then((res) => {
						const cur = this.entries[id]
						if (!cur) return
						if (res.status !== 404 && res.status < 500) {
							console.log('[VIDEO_HLS] ✓ prewarm m3u8 就绪 messageId:', id, 'status:', res.status)
							Vue.set(this.entries, id, { ...cur, status: 'ready', _pollTimer: null })
						} else {
							const t = setTimeout(tick, POLL_INTERVAL)
							Vue.set(this.entries, id, { ...cur, _pollTimer: t })
						}
					})
					.catch(() => {
						const cur = this.entries[id]
						if (!cur) return
						const t = setTimeout(tick, POLL_INTERVAL)
						Vue.set(this.entries, id, { ...cur, _pollTimer: t })
					})
			}
			tick()
		},

		_silentDownload(id, videoUrl, fileName) {
			const entry = this.entries[id]
			if (!entry || entry.downloading || entry.downloadDone) return
			console.log('[VIDEO_HLS] ▶ prewarm 静默下载 messageId:', id)
			Vue.set(this.entries, id, { ...entry, downloading: true })
			try {
				downloader.download({
					url: videoUrl,
					fileName,
					fileType: 'video',
					onStart: () => {},
					onProgress: () => {},
					onComplete: (result) => {
						const cur = this.entries[id]
						if (!cur) return
						const localFile = `file:///${String(result.filePath).replace(/\\/g, '/')}`
						console.log('[VIDEO_HLS] ✓ prewarm 下载完成 messageId:', id)
						Vue.set(this.entries, id, { ...cur, localFile, downloading: false, downloadDone: true })
					},
					onError: (error) => {
						console.warn('[VIDEO_HLS] ✗ prewarm 下载失败 messageId:', id, error)
						const cur = this.entries[id]
						if (cur) Vue.set(this.entries, id, { ...cur, downloading: false })
					}
				})
			} catch (e) {
				console.warn('[VIDEO_HLS] ✗ prewarm 下载调度异常:', e)
				const cur = this.entries[id]
				if (cur) Vue.set(this.entries, id, { ...cur, downloading: false })
			}
		},

		prewarmAllFromChats() {
			if (!Array.isArray(this.chats)) return
			// 调用者需要传 chats，这里保留签名由 action 调用方式处理
		},

		// 扫描所有 chats 的视频消息补启动预热
		prewarmFromChatStore(chatStore) {
			if (!chatStore || !Array.isArray(chatStore.chats)) return
			for (const chat of chatStore.chats) {
				if (!chat || !Array.isArray(chat.messages)) continue
				for (const m of chat.messages) {
					if (m && m.id && m.content && this._looksLikeVideoMessage(m)) {
						this.prewarm(m)
					}
				}
			}
		},

		_looksLikeVideoMessage(msg) {
			// 不依赖 enums，按 content 里的字段判断
			try {
				const c = JSON.parse(msg.content)
				return !!(c.videoUrl || c.m3u8Url)
			} catch (_) { return false }
		}
	},
	getters: {
		getEntry: (state) => (messageId) => {
			if (!messageId) return null
			return state.entries[String(messageId)] || null
		}
	}
})
